import { Loader2 } from "lucide-react";
import type { ButtonHTMLAttributes, ReactNode } from "react";

type ButtonVariant = "primary" | "secondary" | "tonal" | "outline" | "danger" | "ghost" | "link";
type ButtonSize = "sm" | "md" | "lg";

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  pressEffect?: boolean;
};

const variants: Record<ButtonVariant, string> = {
  primary:
    "border border-transparent bg-accent-700 [background:var(--gradient-brand)] text-white shadow-[var(--shadow-button-primary)] hover:-translate-y-px hover:shadow-[var(--shadow-button-primary-hover)] active:translate-y-0 active:bg-accent-800",
  secondary:
    "border border-border bg-surface/70 text-text shadow-none hover:-translate-y-px hover:border-border-strong hover:bg-surface-raised active:translate-y-0 active:bg-surface-sunken",
  tonal:
    "border border-accent-200 bg-accent-50 text-accent-800 shadow-none hover:-translate-y-px hover:border-accent-300 hover:bg-accent-100 active:translate-y-0 active:bg-accent-100",
  outline:
    "border border-border bg-transparent text-text hover:-translate-y-px hover:border-border-strong hover:bg-surface-raised active:translate-y-0 active:bg-surface-sunken",
  danger:
    "border border-danger bg-danger text-white shadow-[var(--shadow-engraved)] hover:shadow-[var(--ring-danger)] active:bg-danger",
  ghost:
    "border border-transparent bg-transparent text-text-muted hover:bg-surface-muted/60 hover:text-text active:bg-surface-sunken",
  link: "border border-transparent bg-transparent px-0 text-link hover:text-text-strong",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-11 min-w-11 rounded-[var(--radius-button-sm)] px-3 text-sm",
  md: "h-12 min-w-12 rounded-[var(--radius-button-md)] px-4 text-sm",
  lg: "h-14 min-w-14 rounded-[var(--radius-button-lg)] px-5 text-base",
};

export function getButtonClassName(
  variant: ButtonVariant = "primary",
  className = "",
  size: ButtonSize = "md",
  pressEffect = !["link", "ghost"].includes(variant),
): string {
  const pressClass = pressEffect ? "active:translate-y-px" : "";
  return `inline-flex items-center justify-center gap-2 font-semibold transition-[background-color,border-color,color,box-shadow,filter,transform] duration-200 ease-out disabled:cursor-not-allowed disabled:opacity-55 focus-visible:outline-none ${pressClass} ${sizes[size]} ${variants[variant]} ${className}`;
}

export function Button({
  children,
  variant = "primary",
  size = "md",
  className = "",
  loading = false,
  iconLeft,
  iconRight,
  pressEffect,
  disabled,
  ...props
}: ButtonProps) {
  return (
    <button
      aria-busy={loading || undefined}
      className={getButtonClassName(variant, className, size, pressEffect)}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : iconLeft}
      <span
        className={`inline-flex items-center gap-2 ${loading ? "opacity-60" : ""}`}
        aria-live={loading ? "polite" : undefined}
      >
        {children}
      </span>
      {loading ? null : iconRight}
    </button>
  );
}
